import type { QueryStatus } from '@tanstack/react-query'

/**
 * Qué tiene que dibujar una pantalla que lista algo que viene de una query.
 *
 * ── POR QUÉ ES UNA FUNCIÓN Y NO UN `&&` EN EL JSX ───────────────────────────
 *
 * Porque escrito a mano en el JSX salió mal en todas las superficies donde se miró, y de tres
 * formas distintas:
 *
 * | Cómo estaba escrito | Qué hacía ante un ERROR |
 * | --- | --- |
 * | `data?.data ?? []` y después «no hay» si la lista está vacía | **Afirma que no hay.** Es lo que hacía la pestaña «Negociaciones» de la ficha: un fallo decía «Este cliente no tiene negociaciones registradas» |
 * | Las dos ramas colgadas de `status === 'success'` | **Nada**: ni lista, ni vacío, ni error. Tarjeta en blanco, como los dos FEIC |
 * | `isLoading \|\| !data ? <Skeleton/>` | **Esqueleto para siempre** (ADR 0031): `isLoading` queda en false y `data` en `undefined` |
 *
 * Las tres son la misma confusión: **«no pude preguntar» y «pregunté y no hay» son hechos
 * distintos**. Uno se reintenta, el otro no. Y la primera forma es la peor, porque no falla:
 * MIENTE, y se lee como un dato.
 *
 * El `QueryClient` de la app **no declara `throwOnError` para las queries** (ADR 0031), así que un
 * error no sube a ningún ErrorBoundary: muere adentro del componente. El que no lo dibuja, no lo
 * muestra.
 *
 * ── LA CUARTA RAMA, QUE CASI NUNCA ESTÁ ─────────────────────────────────────
 *
 * Una query **deshabilitada** —esperando un id que todavía no existe— es `status: 'pending'` con
 * `isLoading` en **false**, porque `isLoading` es `isPending && isFetching`. Ramificar por
 * `isLoading` deja esa espera EN BLANCO. Acá `pending` es esqueleto, esté buscando o esperando
 * turno: en las dos todavía no hay respuesta, y el esqueleto es la respuesta honesta.
 *
 * ── DÓNDE VIVE ──────────────────────────────────────────────────────────────
 *
 * En `shared/` y no en un feature: la regla no sabe de negociaciones ni de documentos. Nació en
 * `features/negotiations/operations/` el 2026-08-05 y se mudó acá al día siguiente, cuando el
 * historial de aprobación —que vive en `client-portal`— necesitó exactamente la misma decisión.
 * Un componente compartido importando de un feature es una flecha al revés.
 */
export type ListView = 'loading' | 'error' | 'empty' | 'list'

export function resolveListView({
  status,
  count
}: {
  /** El `status` de React Query, tal cual. */
  status: QueryStatus
  /** Cuántos elementos llegaron. Sólo se mira cuando la query resolvió bien. */
  count: number
}): ListView {
  if (status === 'error') return 'error'
  if (status === 'pending') return 'loading'
  return count > 0 ? 'list' : 'empty'
}
